import {
  LiveVisitAbdominalAuscultationSection,
  CoughAuscultationSection,
  EarsExaminationSection,
  LiveVisitHeartAusculationSection,
  LiveVisitLungsAsculationSection,
  SkinExaminationSection,
  ThroatExaminationSection,
} from '@higo/common/lib/features/ExaminationPage/sections';
import { AudioTrackGroupProvider } from '@higo/ui/lib/components/AudioPlayer';

import { VisitExaminationPageModel } from '@higo/common/lib/features/ExaminationPage/models';

import { useExaminationDetailsQuery } from '@higo/common/lib/features/ExaminationPage/hooks/queries';
import {
  RequestGenerateLeftEarExaminationVideoButton,
  RequestGenerateRightEarExaminationVideoButton,
  RequestGenerateSkinExaminationVideoButton,
  RequestGenerateThroatExaminationVideoButton,
} from './components/RequestGenerateVisualExaminationVideoButton';
import { LungsFrontAuscultationLegend } from '@higo/common/lib/components/Auscultation/LungsAuscultationLegend/LungsFrontAuscultationLegend';

import { LungsBackAuscultationLegend } from '@higo/common/lib/components/Auscultation/LungsAuscultationLegend/LungsBackAuscultationLegend';
import { HeartAuscultationLegend } from '@higo/common/lib/components/Auscultation/HeartAuscultationLegend/HeartAuscultationLegend';

import { PhysicalExaminationType } from '@higo/api';
import { HeartRateMeasurementEntry } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection/HeartRateMeasurementEntry';
import { TemperatureMeasurementEntry } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection/TemperatureMeasurementEntry';
import { ExaminationPageLayout } from 'pages/ExaminationPage/ExaminationPageLayout';
import { FormattedMessage } from 'react-intl';
import { ExaminationPageSection } from '@higo/common/lib/features/ExaminationPage/sections/ExaminationPageSection';
import styled from 'styled-components';
import { media } from '@higo/ui';
import { useVideoProcessQuery } from 'hooks/queries/useVideoProcessQuery';

const AuscultationGroupContainer = styled.div`
  // display: flex;
  // flex-direction: column;
  // gap: 3rem;
  // ${media.mobile.max} {
  //   gap: 0.5rem;
  // }
`;

const Container = styled.div`
  display: flex;
  column-gap: 1rem;

  ${media.mobile.max} {
    display: grid;
    grid-template-columns: 1fr;
  }
`;

const LegendContainer = styled.div`
  img {
    max-height: 85% !important;
  }
`;

const ListContainer = styled.ul`
  flex-grow: 1;
  list-style: none;
  padding: 0;
  margin: 0;
`;

type VisitsLiveNewWebsocketExamProps = {
  examId: number;
  model: VisitExaminationPageModel;
  lungsAsculationsPoints: any;
  heartAsculationsPoints: any;
  abdominalAsculationsPoints: any;
};

export const VisitsLiveNewWebsocketExam = ({
  examId,
  model,
  lungsAsculationsPoints,
  heartAsculationsPoints,
  abdominalAsculationsPoints,
}: VisitsLiveNewWebsocketExamProps) => {
  const { data: examData, isLoading } = useExaminationDetailsQuery(examId);
  const patientModel = model.patient;

  useVideoProcessQuery(Number(examId));

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!examData) {
    return <div>Live examination error</div>;
  }

  if (
    examData?.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.Throat
  ) {
    return (
      <ExaminationPageLayout>
        <ThroatExaminationSection
          examinationId={examData.examination.id}
          hasTogglingGalleryVideo
          requestGenerateThroatExaminationVideoButtonSlot={
            <RequestGenerateThroatExaminationVideoButton
              examinationId={examData.examination.id}
            />
          }
        />
      </ExaminationPageLayout>
    );
  }

  if (
    examData.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.Skin
  ) {
    return (
      <ExaminationPageLayout>
        <SkinExaminationSection
          hasTogglingGalleryVideo
          examinationId={examData.examination.id}
          requestGenerateSkinExaminationVideoButtonSlot={
            <RequestGenerateSkinExaminationVideoButton
              examinationId={examData.examination.id}
            />
          }
        />
      </ExaminationPageLayout>
    );
  }

  if (
    examData.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.AbdominalAuscultation
  ) {
    return (
      <ExaminationPageSection
        title={
          <FormattedMessage id="examinationPage.section.abdominalAuscultation.title" />
        }
      >
        <AuscultationGroupContainer>
          <AudioTrackGroupProvider>
            <Container id="asculation-group">
              <ListContainer id="asculation-list-container">
                {abdominalAsculationsPoints.map((point: any) => {
                  return (
                    <LiveVisitAbdominalAuscultationSection point={point} />
                  );
                })}
              </ListContainer>
            </Container>
          </AudioTrackGroupProvider>
        </AuscultationGroupContainer>
      </ExaminationPageSection>
    );
  }

  if (
    examData.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.LeftEar
  ) {
    return (
      <ExaminationPageLayout>
        <EarsExaminationSection
          hasTogglingGalleryVideo
          isVisitLeftEarExam
          examinationId={examData.examination.id}
          isPhysicalExaminationCompletedFn={
            model.isPhysicalExaminationCompletedFn
          }
          requestGenerateLeftEarExaminationVideoButtonSlot={
            <RequestGenerateLeftEarExaminationVideoButton
              examinationId={examData.examination.id}
            />
          }
          requestGenerateRightEarExaminationVideoButtonSlot={
            <RequestGenerateRightEarExaminationVideoButton
              examinationId={examData.examination.id}
            />
          }
        />
      </ExaminationPageLayout>
    );
  }

  if (
    examData.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.RightEar
  ) {
    return (
      <ExaminationPageLayout>
        <EarsExaminationSection
          hasTogglingGalleryVideo
          isVisitRightEarExam
          examinationId={examData.examination.id}
          isPhysicalExaminationCompletedFn={
            model.isPhysicalExaminationCompletedFn
          }
          requestGenerateLeftEarExaminationVideoButtonSlot={
            <RequestGenerateLeftEarExaminationVideoButton
              examinationId={examData.examination.id}
            />
          }
          requestGenerateRightEarExaminationVideoButtonSlot={
            <RequestGenerateRightEarExaminationVideoButton
              examinationId={examData.examination.id}
            />
          }
        />
      </ExaminationPageLayout>
    );
  }

  if (
    examData.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.Cough
  ) {
    return (
      <ExaminationPageLayout>
        <CoughAuscultationSection examinationId={examData.examination.id} />
      </ExaminationPageLayout>
    );
  }

  if (
    examData.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.LungsAuscultation
  ) {
    return (
      <ExaminationPageSection
        title={
          <FormattedMessage id="examinationPage.section.lungAuscultation.title" />
        }
      >
        <AuscultationGroupContainer>
          <AudioTrackGroupProvider>
            <Container id="asculation-group">
              <LegendContainer id="legend-container">
                <LungsFrontAuscultationLegend
                  gender={patientModel.gender}
                  maturity={patientModel.maturity}
                />
                <LungsBackAuscultationLegend
                  gender={patientModel.gender}
                  maturity={patientModel.maturity}
                />
              </LegendContainer>

              <ListContainer id="asculation-list-container">
                {lungsAsculationsPoints.map((point: any) => {
                  return (
                    <LiveVisitLungsAsculationSection
                      examinationId={point.examId}
                      patientModel={model.patient}
                      point={point}
                    />
                  );
                })}
              </ListContainer>
            </Container>
          </AudioTrackGroupProvider>
        </AuscultationGroupContainer>
      </ExaminationPageSection>
    );
  }

  if (
    examData.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.HeartAuscultation
  ) {
    return (
      <ExaminationPageSection
        title={
          <FormattedMessage id="examinationPage.section.heartAuscultation.title" />
        }
      >
        <AuscultationGroupContainer>
          <AudioTrackGroupProvider>
            <Container id="asculation-group">
              <LegendContainer id="legend-container">
                <HeartAuscultationLegend
                  gender={patientModel.gender}
                  maturity={patientModel.maturity}
                />
              </LegendContainer>
              <ListContainer id="asculation-list-container">
                {heartAsculationsPoints.map((point: any) => {
                  return (
                    <LiveVisitHeartAusculationSection
                      examinationId={point.examId}
                      patientModel={model.patient}
                      point={point}
                    />
                  );
                })}
              </ListContainer>
            </Container>
          </AudioTrackGroupProvider>
        </AuscultationGroupContainer>
      </ExaminationPageSection>
    );
  }

  if (
    examData.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.HeartRateWav
  ) {
    return (
      <ExaminationPageLayout>
        <HeartRateMeasurementEntry
          isAvailable
          examinationId={examData.examination.id}
        />
      </ExaminationPageLayout>
    );
  }

  if (
    examData.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.Temperature
  ) {
    return (
      <ExaminationPageLayout>
        <TemperatureMeasurementEntry
          isAvailable
          examinationId={examData.examination.id}
        />
      </ExaminationPageLayout>
    );
  }

  return null;
};
